/**
 * 组织架构管理 API
 * 对应 foundation-service (7092)
 */

import { unifiedApi } from '@/utils/unifiedApi';
import handleApiResponse from '@/utils/apiResponseHandler';
import type {
  Organization,
  OrganizationQueryParams,
  OrganizationFormData,
  OrganizationTreeNode,
  OrganizationMember,
  OrganizationStatus,
  MemberQueryParams,
  MemberOperationData,
  Position,
  OrganizationStatistics,
} from './types';

// ==================== 组织管理 ====================

/** 获取组织列表 */
export const getOrganizationList = (params?: OrganizationQueryParams) =>
  handleApiResponse(
    unifiedApi.service('organization').get<{ data: Organization[]; count: number }>('/organizations', { params })
  );

/** 获取组织树 */
export const getOrganizationTree = (params?: { includeMembers?: boolean; status?: OrganizationStatus }) =>
  handleApiResponse(
    unifiedApi.service('organization').get<{ data: OrganizationTreeNode[]; count: number }>('/organizations/tree', { params })
  );

/** 获取组织详情 */
export const getOrganizationDetail = (id: string) =>
  handleApiResponse(
    unifiedApi.service('organization').get<{ data: Organization; count: number }>(`/organizations/${id}`)
  );

/** 创建组织 */
export const createOrganization = (data: OrganizationFormData) =>
  handleApiResponse(
    unifiedApi.service('organization').post<{ data: Organization; count: number }>('/organizations', data)
  );

/** 更新组织 */
export const updateOrganization = (id: string, data: Partial<OrganizationFormData>) =>
  handleApiResponse(
    unifiedApi.service('organization').put<{ data: Organization; count: number }>(`/organizations/${id}`, data)
  );

/** 删除组织 */
export const deleteOrganization = (id: string) =>
  handleApiResponse(
    unifiedApi.service('organization').delete<{ data: null; count: number }>(`/organizations/${id}`)
  );

/** 批量删除组织 */
export const batchDeleteOrganization = (ids: string[]) =>
  handleApiResponse(
    unifiedApi.service('organization').post<{ data: null; count: number }>('/organizations/batch/delete', { ids })
  );

// ==================== 组织排序 ====================

/** 更新组织排序 */
export const updateOrganizationSort = (organizations: Array<{ id: string; sort: number; parentId?: string }>) =>
  handleApiResponse(
    unifiedApi.service('organization').post<{ data: null; count: number }>('/organizations/sort', { organizations })
  );

/** 移动组织 */
export const moveOrganization = (id: string, targetParentId?: string, targetSort?: number) =>
  handleApiResponse(
    unifiedApi.service('organization').post<{ data: null; count: number }>(`/organizations/${id}/move`, { targetParentId, targetSort })
  );

/** 复制组织 */
export const copyOrganization = (id: string, name: string, code: string) =>
  handleApiResponse(
    unifiedApi.service('organization').post<{ data: Organization; count: number }>(`/organizations/${id}/copy`, { name, code })
  );

// ==================== 组织状态管理 ====================

/** 激活组织 */
export const activateOrganization = (id: string) =>
  handleApiResponse(
    unifiedApi.service('organization').post<{ data: null; count: number }>(`/organizations/${id}/activate`)
  );

/** 停用组织 */
export const deactivateOrganization = (id: string) =>
  handleApiResponse(
    unifiedApi.service('organization').post<{ data: null; count: number }>(`/organizations/${id}/deactivate`)
  );

/** 解散组织 */
export const dissolveOrganization = (id: string, reason?: string) =>
  handleApiResponse(
    unifiedApi.service('organization').post<{ data: null; count: number }>(`/organizations/${id}/dissolve`, { reason })
  );

// ==================== 组织成员管理 ====================

/** 获取组织成员 */
export const getOrganizationMembers = (organizationId: string, params?: MemberQueryParams) =>
  handleApiResponse(
    unifiedApi.service('organization').get<{ data: { list: OrganizationMember[]; total: number }; count: number }>(
      `/organizations/${organizationId}/users`,
      { params }
    )
  );

/** 添加成员到组织 */
export const addMembersToOrganization = async (organizationId: string, data: MemberOperationData) => {
  const userIds = data.userIds || [];
  if (!userIds.length) {
    return { data: null, count: 0 };
  }

  // Backend AddUser accepts one user per request with snake_case payload.
  // Keep the UI multi-select behavior by submitting each selected user.
  const results = await Promise.all(
    userIds.map((userId) =>
      handleApiResponse(
        unifiedApi.service('organization').post<{ data: null; count: number }>(
          `/organizations/${organizationId}/users`,
          {
            user_id: Number(userId),
            is_primary: false,
          }
        )
      )
    )
  );

  return {
    data: null,
    count: results.length,
  };
};

/** 从组织移除成员 */
export const removeMembersFromOrganization = (organizationId: string, userIds: string[]) =>
  handleApiResponse(
    unifiedApi.service('organization').post<{ data: null; count: number }>(`/organizations/${organizationId}/users/remove`, { userIds })
  );

/** 转移成员 */
export const transferMembers = (sourceOrgId: string, targetOrgId: string, userIds: string[]) =>
  handleApiResponse(
    unifiedApi.service('organization').post<{ data: null; count: number }>(
      `/organizations/${sourceOrgId}/users/transfer`,
      { targetOrgId, userIds }
    )
  );

/** 更新成员职位 */
export const updateMemberPosition = (
  organizationId: string,
  userId: string,
  data: { positionId?: string; jobTitle?: string; isLeader?: boolean; isAssistant?: boolean }
) =>
  handleApiResponse(
    unifiedApi.service('organization').put<{ data: null; count: number }>(
      `/organizations/${organizationId}/users/${userId}`,
      data
    )
  );

/** 添加员工到组织 */
export const addEmployeeToOrganization = (payload: { dept_id: string; user_id: string; roles: string[] }) =>
  handleApiResponse(
    unifiedApi.service('organization').post<{ data: null; count: number }>('/organizations/users', payload)
  );

// ==================== 领导管理 ====================

/** 设置组织领导 */
export const setOrganizationLeader = (organizationId: string, userId: string) =>
  unifiedApi.service('organization').post(`/organizations/${organizationId}/leader`, { userId });

/** 移除组织领导 */
export const removeOrganizationLeader = (organizationId: string) =>
  unifiedApi.service('organization').delete(`/organizations/${organizationId}/leader`);

/** 设置组织助理 */
export const setOrganizationAssistants = (organizationId: string, userIds: string[]) =>
  unifiedApi.service('organization').post(`/organizations/${organizationId}/assistants`, { userIds });

// ==================== 组织职位管理 ====================

/** 获取组织职位列表 */
export const getOrgPositions = (params?: {
  current?: number;
  pageSize?: number;
  name?: string;
  code?: string;
  level?: number;
  status?: string;
}) =>
  unifiedApi.service('organization').get<{ list: Position[]; total: number }>('/positions', { params });

/** 获取组织职位详情 */
export const getOrgPositionDetail = (id: string) =>
  unifiedApi.service('organization').get<Position>(`/positions/${id}`);

/** 创建组织职位 */
export const createOrgPosition = (data: {
  name: string;
  code: string;
  level: number;
  description?: string;
  responsibilities?: string[];
  requirements?: string[];
  salaryRange?: { min: number; max: number };
}) =>
  unifiedApi.service('organization').post<Position>('/positions', data);

/** 更新组织职位 */
export const updateOrgPosition = (id: string, data: Partial<Position>) =>
  unifiedApi.service('organization').put<Position>(`/positions/${id}`, data);

/** 删除组织职位 */
export const deleteOrgPosition = (id: string) =>
  unifiedApi.service('organization').delete(`/positions/${id}`);

// ==================== 用户组织关系 ====================

/** 获取用户所属组织 */
export const getUserOrganizations = (userId: string) =>
  unifiedApi.service('organization').get<Organization[]>(`/users/${userId}/organizations`);

/** 获取用户在组织中的职位 */
export const getUserPosition = (userId: string, organizationId: string) =>
  unifiedApi.service('organization').get<{
    position?: Position;
    jobTitle?: string;
    isLeader: boolean;
    isAssistant: boolean;
  }>(`/users/${userId}/organizations/${organizationId}/position`);

// ==================== 组织层级关系 ====================

/** 获取组织路径 */
export const getOrganizationPath = (id: string) =>
  unifiedApi.service('organization').get<Organization[]>(`/organizations/${id}/path`);

/** 获取组织子级 */
export const getOrganizationChildren = (id: string, deep?: boolean) =>
  unifiedApi.service('organization').get<Organization[]>(`/organizations/${id}/children`, { params: { deep } });

/** 获取组织父级 */
export const getOrganizationParents = (id: string) =>
  unifiedApi.service('organization').get<Organization[]>(`/organizations/${id}/parents`);

// ==================== 统计信息 ====================

/** 获取组织统计 */
export const getOrganizationStatistics = (params?: { dateRange?: [string, string]; organizationId?: string }) =>
  unifiedApi.service('organization').get<OrganizationStatistics>('/organizations/statistics', { params });

/** 获取组织成员统计 */
export const getOrganizationMemberStatistics = (organizationId: string) =>
  unifiedApi.service('organization').get<{
    totalMembers: number;
    newMembers: number;
    leftMembers: number;
    positionDistribution: Array<{ positionName: string; count: number }>;
    departmentDistribution: Array<{ departmentName: string; count: number }>;
  }>(`/organizations/${organizationId}/member-statistics`);

// ==================== 导入导出 ====================

/** 导入组织 */
export const importOrganizations = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return unifiedApi.service('organization').post<{ success: number; failed: number; errors: string[] }>(
    '/organizations/import',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
};

/** 导出组织 */
export const exportOrganizations = (ids?: string[]) =>
  unifiedApi.service('organization').post('/organizations/export', { ids }, { responseType: 'blob' });

/** 导出组织架构图 */
export const exportOrganizationChart = (id: string, format: 'png' | 'pdf' | 'svg') =>
  unifiedApi.service('organization').post(`/organizations/${id}/chart/export`, { format }, { responseType: 'blob' });

// ==================== 验证 ====================

/** 检查组织编码 */
export const checkOrganizationCode = (code: string, excludeId?: string) =>
  unifiedApi.service('organization').get<{ available: boolean; suggestion?: string }>(
    '/organizations/check-code',
    { params: { code, excludeId } }
  );

/** 检查组织名称 */
export const checkOrganizationName = (name: string, parentId?: string, excludeId?: string) =>
  unifiedApi.service('organization').get<{ available: boolean; suggestion?: string }>(
    '/organizations/check-name',
    { params: { name, parentId, excludeId } }
  );

// ==================== 组织架构图 ====================

/** 获取组织架构图 */
export const getOrganizationChart = (id: string) =>
  unifiedApi.service('organization').get<{
    nodes: Array<{
      id: string;
      name: string;
      type: number;
      leader?: string;
      memberCount: number;
      x?: number;
      y?: number;
    }>;
    edges: Array<{ source: string; target: string }>;
  }>(`/organizations/${id}/chart`);

/** 组织 API 对象 */
export const organizationApi = {
  // 组织管理
  getOrganizationList,
  getOrganizationTree,
  getOrganizationDetail,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  batchDeleteOrganization,
  // 组织排序
  updateOrganizationSort,
  moveOrganization,
  copyOrganization,
  // 组织状态管理
  activateOrganization,
  deactivateOrganization,
  dissolveOrganization,
  // 组织成员管理
  getOrganizationMembers,
  addMembersToOrganization,
  removeMembersFromOrganization,
  transferMembers,
  updateMemberPosition,
  addEmployeeToOrganization,
  // 领导管理
  setOrganizationLeader,
  removeOrganizationLeader,
  setOrganizationAssistants,
  // 组织职位管理
  getOrgPositions,
  getOrgPositionDetail,
  createOrgPosition,
  updateOrgPosition,
  deleteOrgPosition,
  // 用户组织关系
  getUserOrganizations,
  getUserPosition,
  // 组织层级关系
  getOrganizationPath,
  getOrganizationChildren,
  getOrganizationParents,
  // 统计信息
  getOrganizationStatistics,
  getOrganizationMemberStatistics,
  // 导入导出
  importOrganizations,
  exportOrganizations,
  exportOrganizationChart,
  // 验证
  checkOrganizationCode,
  checkOrganizationName,
  // 组织架构图
  getOrganizationChart,
};

export default organizationApi;
